
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Employee {
    id_employee: string;
    full_name: string;
    birthday: string;
    role: string;
    salary: number;
    admission_date: string;
    phone_number: string;
    type: string;
    isactive: boolean;
    // fk_companies_id_company: string;
    fk_subsidiary_id_subsidiary: string;
    fk_trips_id_trip?: string;
}
type EmployeeInput = Omit<Employee, 'id_employee'>

type EmployeePayload = Omit<Employee, 'id_employee' | 'isactive' | 'fk_trips_id_trip'>;

interface EmployeesProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
    data?: any;
}

interface EmployeesContextData {
    createEmployee: (employee: EmployeeInput) => Promise<ResponseProps>;
    updateEmployee: (employee: Employee) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
    getTripEmployees: (id_trip: string) => Promise<ResponseProps>;
    getEmployeeRemunerationStatement: (id: string | number, filter: EmployeeRemunerationStatementFilter) => Promise<ResponseProps>;
}

export interface EmployeeRemunerationStatementFilter {
    from: string;
    to: string;
    page?: number;
    pageSize?: number;
    category?: 'PRODUCTION' | 'COMMISSION' | 'BONUS';
}

interface UseEmployeesOptions {
    loadEmployees?: boolean;
}

interface UseEmployeesData extends EmployeesContextData {
    employees: Employee[];
}

const EmployeesContext = createContext<EmployeesContextData>(
    {} as EmployeesContextData
);

const employeesQueryKey = ['employees'];
const tripEmployeesQueryKey = ['employees', 'trip'];
const emptyEmployees: Employee[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchEmployees() {
  const response = await apiV2.get('employees');
  return Array.isArray(response?.data) ? response.data : [];
}

async function fetchTripEmployees(id_trip: string) {
  const params = {
    fk_trips: id_trip
  };

  const response = await api.get('/employeesintrip', { params: { ...api.defaults.params, ...params } });

  if (response.status === 200 && !response.data.error) {
    return response?.data;
  }

  throw new Error(response.data && response.data.error ? response.data.error : "Erro ao buscar dados dos tripulantes da viagem.");
}

function translateEmployeeApiMessage(message: unknown, fallback: string) {
  if (typeof message !== 'string') return fallback;
  const normalizedMessage = message.trim();

  if (/^employee not found\.?$/i.test(normalizedMessage)) return 'Colaborador não encontrado.';
  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) return 'Você não tem permissão para realizar esta ação.';
  if (/^from must be before or equal to to\.?$/i.test(normalizedMessage)) return 'A data inicial deve ser anterior ou igual à data final.';
  if (/^expected a valid date in yyyy-mm-dd format\.?$/i.test(normalizedMessage) || /^validation failed\.?$/i.test(normalizedMessage)) return 'Informe um intervalo de datas válido.';
  if (/^unauthorized\.?$/i.test(normalizedMessage)) return 'Sua sessão expirou. Faça login novamente.';
  if (/^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(normalizedMessage)) return fallback;

  return normalizedMessage;
}

export function EmployeesProvider({ children }: EmployeesProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateEmployees() {
    queryClient.invalidateQueries({ queryKey: employeesQueryKey });
    queryClient.invalidateQueries({ queryKey: tripEmployeesQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/employees/${id}`);
    if (response.status === 200) {
      invalidateEmployees();
      return {
        success: true,
        message: "Funcionário removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover funcionário."
    };
  }

  function normalizeEmployeePayload(employee: EmployeeInput | Employee): EmployeePayload {
    return {
      full_name: employee.full_name,
      birthday: employee.birthday,
      role: employee.role,
      salary: Number(employee.salary),
      admission_date: employee.admission_date,
      phone_number: employee.phone_number,
      type: employee.type,
      fk_subsidiary_id_subsidiary: employee.fk_subsidiary_id_subsidiary
    };
  }

  async function createEmployee(employeesInput: EmployeeInput) {
    const response = await apiV2.post('/employees', normalizeEmployeePayload(employeesInput));
    if (response.status === 201) {
      invalidateEmployees();
      return {
        success: true,
        message: "Funcionário adicionada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar funcionário."
    };

  }

  async function updateEmployee(employee: Employee) {
    const response = await apiV2.put(`/employees/${employee.id_employee}`, normalizeEmployeePayload(employee));
    if (response.status === 204) {
      invalidateEmployees();
      return {
        success: true,
        message: "Funcionário editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar funcionário."
    };
  }

  async function getTripEmployees(id_trip: string) {
    try {
      const tripEmployees = await queryClient.fetchQuery({
        queryKey: [...tripEmployeesQueryKey, id_trip],
        queryFn: () => fetchTripEmployees(id_trip),
        staleTime: 5 * 60 * 1000
      });

      return {
        success: true,
        message: "Ok!",
        data: tripEmployees
      };
    } catch (err: any) {
      return {
        success: false,
        message: err?.message || "Erro ao buscar dados dos tripulantes da viagem.",
        data: null
      };
    }
  }

  async function getEmployeeRemunerationStatement(id: string | number, filter: EmployeeRemunerationStatementFilter) {
    const response: any = await apiV2.get(`/employees/${id}/remuneration-statement`, { params: filter });
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;
    const message = response?.data?.message || response?.message;

    return {
      success,
      message: success
        ? 'Extrato de remuneração consultado com sucesso!'
        : translateEmployeeApiMessage(message, 'Não foi possível consultar o extrato de remuneração.'),
      data: success ? response?.data?.data : undefined
    };
  }

  return (
    <EmployeesContext.Provider value={{ createEmployee, updateEmployee, updateOnDelete, getTripEmployees, getEmployeeRemunerationStatement }}>
      {children}
    </EmployeesContext.Provider>
  );
}

export function UseEmployees(options: UseEmployeesOptions = {}): UseEmployeesData {
  const context = useContext(EmployeesContext);
  const { loadEmployees = false } = options;

  const employeesQuery = useQuery({
    queryKey: employeesQueryKey,
    queryFn: fetchEmployees,
    enabled: loadEmployees && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    employees: employeesQuery.data || emptyEmployees
  };
}
